import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { Calendar, Newspaper, ChevronRight } from 'lucide-react';
import '../archive/timeline_archieve.css';

const TimelineArchive = () => {
  const navigate = useNavigate();
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    const fetchTimelineContent = async () => {
      try {
        setLoading(true);
        
        // 1. Fetch Featured Events
        const eventQuery = query(collection(db, "parmEvent"), where("event_timeline_page", "==", true));
        const eventSnap = await getDocs(eventQuery);
        const events = eventSnap.docs.map(doc => {
        const data = doc.data();
        
        // 🟢 FIXED: Safely resolve the year regardless of whether it's a Timestamp, Date, or String
        let year = 'Unknown';
        let dateDisplay = 'N/A';

        if (data.event_date) {
            if (typeof data.event_date === 'object' && 'seconds' in data.event_date) {
            // It's a Firestore Timestamp
            const d = new Date(data.event_date.seconds * 1000);
            year = d.getFullYear().toString();
            dateDisplay = d.toLocaleDateString('en-GB', { timeZone: 'UTC' });
            } else if (data.event_date instanceof Date) {
            // It's a standard JS Date object
            year = data.event_date.getFullYear().toString();
            dateDisplay = data.event_date.toLocaleDateString('en-GB', { timeZone: 'UTC' });
            } else {
            // Fallback if it actually arrived as a string representation (e.g., "2026-07-27")
            const dateStr = String(data.event_date);
            year = dateStr.split('-')[0] || 'Unknown';
            dateDisplay = dateStr;
            }
        }

        return {
            id: doc.id,
            type: 'event',
            title: data.event_title || 'Untitled Event',
            description: data.event_description || '',
            image: data.event_image_array?.[0] || '',
            dateDisplay: dateDisplay,
            year: year
        };
        });

        // 2. Fetch Featured News
        const newsQuery = query(collection(db, "parmNews"), where("news_timeline_page", "==", true));
        const newsSnap = await getDocs(newsQuery);
        const news = newsSnap.docs.map(doc => {
          const data = doc.data();
          // Extract year from createdAt timestamp or string fallback
          let year = 'Unknown';
          if (data.createdAt?.seconds) {
            year = new Date(data.createdAt.seconds * 1000).getFullYear().toString();
          } else if (data.news_date) {
            year = data.news_date.split('-')[0];
          }
          return {
            id: doc.id,
            type: 'news',
            title: data.news_title || 'Untitled News',
            description: data.news_description || '',
            image: data.news_image_array?.[0] || '',
            dateDisplay: year,
            year: year
          };
        });

        // 3. Combine both collections
        const combined = [...events, ...news];
        setTimelineData(combined);

      } catch (error) {
        console.error("Error aggregating timeline content layouts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTimelineContent();
  }, []);

  // 4. Group data by Year and limit items per year to maximum of 3 elements
  const groupedByYear = useMemo(() => {
    const groups = {};
    
    timelineData.forEach(item => {
      if (item.year === 'Unknown') return;
      if (!groups[item.year]) groups[item.year] = [];
      
      // Limit to 3 items max per year row context
      if (groups[item.year].length < 3) {
        groups[item.year].push(item);
      }
    });

    // Sort years descending or ascending as preferred
    return Object.keys(groups)
      .sort((a, b) => Number(b) - Number(a)) 
      .reduce((obj, key) => {
        obj[key] = groups[key];
        return obj;
      }, {});
  }, [timelineData]);

  // Handle auto-selection of the first year once data loads
  const sortedYears = Object.keys(groupedByYear);
  useEffect(() => {
    if (sortedYears.length > 0 && !selectedYear) {
      setSelectedYear(sortedYears[0]);
    }
  }, [sortedYears, selectedYear]);

  // 🟢 3. Dynamic route execution handler
  const handleCardClick = (id, type) => {
    if (type === 'event') {
      navigate(`/archive-event-detail-parm/${id}`);
    } else if (type === 'news') {
      navigate(`/archive-news-detail-parm/${id}`);
    }
  };

  if (loading) return <div className="timeline-loader">Building structural archive timeline...</div>;
  if (sortedYears.length === 0) return <div className="timeline-empty">No featured timeline items found.</div>;

  return (
    <div className="timeline-section-wrapper">
      <div className="timeline-main-container">
        
        {/* Dynamic Display Stage: Displays cards inside the selected year track */}
        <div className="timeline-display-stage">
          <div className="stage-meta-info">
            <span className="stage-label">YEAR SHOT</span>
            <h1 className="stage-big-year">{selectedYear}</h1>
          </div>

          <div className="timeline-cards-grid">
            {groupedByYear[selectedYear]?.map((item) => (
              <div 
              key={item.id} 
              className="timeline-content-card"
              onClick={() => handleCardClick(item.id, item.type)}
              >
                <div className="card-image-wrapper">
                  <img src={item.image} alt={item.title} />
                  <span className={`content-badge ${item.type}`}>
                    {item.type === 'event' ? <Calendar size={12} /> : <Newspaper size={12} />}
                    {item.type.toUpperCase()}
                  </span>
                </div>
                <div className="card-body-details">
                  <h3>{item.title}</h3>
                  <p className="card-truncate-desc">
                    {item.description.replace(/<[^>]*>/g, '').substring(0, 100)}...
                  </p>
                  <div className="card-action-link">
                    <span>Read More</span>
                    <ChevronRight size={14} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Slider Axis Rule: The bottom navigation track */}
        <div className="timeline-axis-navigation">
          <div className="axis-line"></div>
          <div className="axis-years-container">
            {sortedYears.map((year) => (
              <button
                key={year}
                className={`axis-year-node ${selectedYear === year ? 'active' : ''}`}
                onClick={() => setSelectedYear(year)}
              >
                <span className="node-dot"></span>
                <span className="node-text">{year}</span>
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default TimelineArchive;